import { ODataEntitySetService, ODataServiceFactory } from 'angular-odata';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FilterModel, IServerSideGetRowsParams, SortModelItem } from 'ag-grid-community';
import {
  convertToFilterString,
  convertToOrderByString,
  CustomFilterRule,
  CustomSortRule,
  SetFilterRule,
} from '@workspace-app/shared';
import { CoaterLicense } from '@workspace-app/odatabackend/QualicoatBackendModel';

@Injectable({
  providedIn: 'root',
})
export class LicenseDataService {
  private factory = inject(ODataServiceFactory);
  private httpClient = inject(HttpClient);

  licenseNumberService: ODataEntitySetService<CoaterLicense>;
  
  constructor() {
    this.licenseNumberService = this.factory.entitySet<CoaterLicense>(
      'Licenses',
      'QualicoatBackend.Features.Certification.Licenses.QualicoatLicense'
    );
  }

  public getRows(params: IServerSideGetRowsParams) {
    const endRow = params.request.endRow || 20;
    const startRow = params.request.startRow || 0;
    // Create a fresh entities resource for each query to avoid accumulating state
    const freshEntities = this.licenseNumberService.entities();
    return freshEntities
      ?.query((q) => {
        q.expand('ProductionSite($expand=Customer($expand=AssignedGl),Contact),LastReview');
        q.top(endRow);
        q.skip(startRow);
        if (params.request.sortModel?.length > 0) q.orderBy(this.convertToOrderByString(params.request.sortModel));
        if (params.request.filterModel) q.filter(this.convertToFilterString(params.request.filterModel));
      })
      .fetch({ withCount: true })
      .subscribe((response) => {
        const count = response.annots['annotations'].get('@odata.count');
        if (response.entities) {
          return params.success({ rowData: response.entities, rowCount: count });
        }
        return params.fail();
      });
  }

  withdrawLicense(licenseNumberId: string, withdrawalDate: Date, withdrawalReason: string) {
    // Use traditional REST API endpoint for license updates
    return this.httpClient.put(`api/licenses/${licenseNumberId}`, {
      withdrawalDate: withdrawalDate.toISOString(),
      withdrawnBy: 'currentUser', // Current User
      withdrawalReason: withdrawalReason,
    });
  }

  grantLicense(licenseNumberId: string, expiryDate: Date | null) {
    // Use traditional REST API endpoint for license updates
    return this.httpClient.put(`api/licenses/${licenseNumberId}`, {
      expiryDate: expiryDate ? expiryDate.toISOString() : null,
      withdrawalDate: null,
      withdrawnBy: null,
      withdrawalReason: null,
    });
  }

  private convertToOrderByString(sortModel: SortModelItem[]) {
    const customSortRules: CustomSortRule[] = [
      {
        key: 'productionSite.customer.assignedGl.name',
        fn: (_key: string, sortOrder: 'asc' | 'desc') =>
          `ProductionSite/Customer/AssignedGl/Name ${sortOrder}`,
      },
      {
        key: 'completeLicenseNumber',
        fn: (_key: string, sortOrder: 'asc' | 'desc') => {
          // Sort logic for license numbers:
          // 1. First by isGranting (granting licenses like "GR123" come first/last)
          // 2. Then by grantingNumber for granting licenses
          // 3. Then by prefix for regular licenses
          // 4. Finally by number
          return `isGranting ${sortOrder}, grantingNumber ${sortOrder}, prefix ${sortOrder}, number ${sortOrder}`;
        },
      },
    ];
    return convertToOrderByString({ sortModel, customSortRules });
  }

  private convertToFilterString(filterModel: FilterModel) {
    const customFilterRules: CustomFilterRule[] = [
      {
        key: 'completeLicenseNumber',
        fn: (_key: string, _filterType: string, type: string, filter: string) => {
          let prefix: string | null = null;
          let number: string | null = null;
          if (filter.includes('-')) {
            prefix = filter.split('-')[0];
            number = filter.split('-')[1];
          } else {
            if (this.isNumeric(filter)) {
              number = filter;
            } else {
              prefix = filter;
            }
          }
          const subFilters: string[] = [];
          if (type == 'contains') {
            if (prefix) subFilters.push(`(contains(tolower(prefix), tolower('${prefix}')))`);
            if (number) subFilters.push(`(contains(cast(Number, 'Edm.String'), '${number}'))`);
          } else if (type == 'equals') {
            if (prefix) subFilters.push(`(tolower(prefix) eq tolower('${prefix}'))`);
            if (number) subFilters.push(`(Number eq ${number})`);
          }
          // Return empty string if no filters match, otherwise wrap in parentheses
          return subFilters.length > 0 ? `(${subFilters.join(' and ')})` : '';
        },
      },
    ];
    const setFilterRules: SetFilterRule[] = [
      {
        key: 'renewalDisplayStatus',
        fn: (_key: string, values: string[]) => {
          const subFilters: string[] = [];
          values.forEach((value) => {
            if (value === 'In Progress') {
              // Created or Review states
              subFilters.push(`(LastReview/CurrentState eq 'Created' or LastReview/CurrentState eq 'Review')`);
            } else if (value === 'Certificate Ready' || value === 'Certificate Sent') {
              // All states where certificates are ready or sent
              // Backend now returns "Certificate Ready" for all these states
              subFilters.push(
                `(LastReview/CurrentState eq 'IssuingCertificates' or LastReview/CurrentState eq 'FinalReview' or LastReview/CurrentState eq 'Completed')`
              );
            } else if (value === 'Cancelled') {
              subFilters.push(`(LastReview/CurrentState eq 'Cancelled')`);
            }
          });
          return subFilters.length > 0 ? `(${subFilters.join(' or ')})` : '';
        },
      },
      {
        key: 'seaside',
        fn: (_key: string, values: unknown[]) => {
          const subFilters: string[] = [];
          values.forEach((value) => {
            // Handle different value types: boolean, string, or null
            if (value === null || value === 'null' || value === 'unknown') {
              // For "Unknown" (null), match only null values
              subFilters.push(`ProductionSite/CoatingLines/any(cl: cl/Seaside eq null)`);
            } else if (typeof value === 'boolean') {
              if (value) {
                // For "Seaside" (true), only match true values
                subFilters.push(`ProductionSite/CoatingLines/any(cl: cl/Seaside eq true)`);
              } else {
                // For "No Seaside" (false), match only false values (not null)
                subFilters.push(`ProductionSite/CoatingLines/all(cl: cl/Seaside eq false)`);
              }
            } else if (typeof value === 'string') {
              const strValue = value.toLowerCase();
              if (strValue === 'true') {
                // For "Seaside" (true), only match true values
                subFilters.push(`ProductionSite/CoatingLines/any(cl: cl/Seaside eq true)`);
              } else if (strValue === 'false') {
                // For "No Seaside" (false), match only false values (not null)
                subFilters.push(`ProductionSite/CoatingLines/all(cl: cl/Seaside eq false)`);
              } else if (strValue === 'null' || strValue === 'unknown') {
                // For "Unknown" (null), match only null values
                subFilters.push(`ProductionSite/CoatingLines/any(cl: cl/Seaside eq null)`);
              }
            }
          });
          return subFilters.length > 0 ? `(${subFilters.join(' or ')})` : '';
        },
      },
      {
        key: 'class',
        fn: (_key: string, values: string[]) => {
          const subFilters: string[] = [];
          values.forEach((value) => {
            subFilters.push(`class eq ${value}`);
          });
          return `(${subFilters.join(' or ')})`;
        },
      },
      {
        key: 'licenseType',
        fn: (_key: string, values: string[]) => {
          const subFilters: string[] = [];
          values.forEach((value) => {
            subFilters.push(`LicenseType eq '${value}'`);
          });
          return `(${subFilters.join(' or ')})`;
        },
      },
      {
        key: 'productionSite.customer.assignedGl.name',
        fn: (_key: string, values: string[]) => {
          const subFilters = values.map(v => `ProductionSite/Customer/AssignedGl/Name eq '${v}'`);
          return subFilters.length > 0 ? `(${subFilters.join(' or ')})` : '';
        },
      },
      {
        key: 'licenseState',
        fn: (_key: string, values: string[]) => {
          const subFilters: string[] = [];
          const now = new Date();
          // Format date as yyyy-M-D without quotes (OData format)
          const dateString = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
          // Expiring licenses are those expiring within the next month
          const nextMonthDate = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate());
          const nextMonthDateString = `${nextMonthDate.getFullYear()}-${nextMonthDate.getMonth() + 1}-${nextMonthDate.getDate()}`;

          values.forEach((value) => {
            if (value === 'Withdrawn') {
              subFilters.push(`(withdrawalDate ne null)`);
            } else if (value === 'Expiring') {
              // Expiring = has expiry date within the next month
              subFilters.push(`(expiryDate ne null and expiryDate gt ${dateString} and expiryDate le ${nextMonthDateString})`); 
            } else if (value === 'Expired') {
              // Expired = has expiry date in the past or equal to now
              subFilters.push(`(expiryDate ne null and expiryDate le ${dateString})`);
            } else if (value === 'Granting') {
              subFilters.push(`(isGranting eq true)`);
            } else if (value === 'Valid') {
              // Valid = no withdrawal date, not granting, and either no expiry date or expiry date in the future
              subFilters.push(`(withdrawalDate eq null and isGranting eq false and (expiryDate eq null or expiryDate gt ${dateString}))`);
            }
          });

          const result = subFilters.length > 0 ? `(${subFilters.join(' or ')})` : '';
          return result;
        },
      },
    ];
    return convertToFilterString({ filterModel, setFilterRules, customFilterRules });
  }

  private isNumeric(value: string) {
    return /^\d+$/.test(value);
  }
}
